import React, { useState } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { PAGE_SIZE } from 'constants/Common';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppSearch from '@tenant/core/AppSearchBar';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HandymanIcon from '@mui/icons-material/Handyman';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { StatusAppType } from 'constants/AppEnums';
import useGetListAppInstance from 'redux/slices/appInstance/hooks/useGetListAppInstance';
import { useSelector } from 'react-redux';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import useModal from '@tenant/utility/hooks/useModal';
import useDeleteAppInstance from 'redux/slices/appInstance/hooks/useDeleteAppInstance';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import DeleteAppInstanceAction from '../Common/DeleteAppInstanceAction';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import RefreshIcon from '@mui/icons-material/Refresh';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';

export const StatusApp = {
  [StatusAppType.IN_ACTIVE]: {
    label: 'In Active',
    color: 'error',
    icon: <CancelIcon />,
  },
  [StatusAppType.ACTIVE]: {
    label: 'Active',
    color: 'info',
    icon: <RocketLaunchIcon />,
  },
  [StatusAppType.COMING_SOON]: {
    label: 'Coming Soon',
    color: 'warning',
    icon: <HourglassEmptyIcon />,
  },
  [StatusAppType.DRAFT]: {
    label: 'Draft',
    color: 'warning',
    icon: <HandymanIcon />,
  },
};

const AppInstanceTable = () => {
  const nextTick = useNextTick();
  const canDelete = useAllowedAccess([AuthorizePermission.APP_INSTANCE_DELETE]);
  const { filter } = useSelector((s) => s.appInstance);
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
  const [pageIndex, setPageIndex] = useState(
    filter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  );
  const [searchTerm, setSearchTerm] = useState(filter?.searchTerm ?? '');
  const [isSearchTermChanged, setIsSearchTermChanged] = useState(false);
  const [selected, setSelected] = useState();
  const { visible, onClose, onShow } = useModal();
  const { isLoading, isRefetching, refetch } = useGetListAppInstance({
    pageSize,
    pageIndex,
    searchTerm,
  });
  const loading = isLoading || isRefetching;
  const { list } = useSelector((s) => s.appInstance);
  const totalCount = list?.TotalCount ?? 0;
  const navigate = useNavigate();
  const rows = list?.Items ?? [];
  const deleteMutate = useDeleteAppInstance();

  const clearSearchTerm = async () => {
    setSearchTerm('');
    await nextTick();
    refetch();
  };

  const onChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    setPageIndex(DEFAULT_PAGE_INDEX);
    setIsSearchTermChanged(true);
  };

  const handleCloseDelete = () => {
    setSelected();
    onClose();
  };
  const handleShowDelete = (item) => {
    setSelected(deepClone(item));
    onShow();
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Name',
      width: 400,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Link to={ROUTE_STRING.APP_INSTANCE.VIEW.replace(':id', params.row.Id)}>
          {params.value}
        </Link>
      ),
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          sx={{ minWidth: 150 }}
          key='action--view'
          label='View'
          showInMenu
          onClick={() => {
            navigate(ROUTE_STRING.APP_INSTANCE.LIST + '/' + params.row.Id);
          }}
        />,
        <GridActionsCellItem
          key='action--delete'
          label='Delete'
          showInMenu={canDelete}
          onClick={() => {
            handleShowDelete(params.row);
          }}
        />,
      ],
    },
  ];

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  const goToAdd = () => {
    navigate(ROUTE_STRING.APP_INSTANCE.ADD);
  };

  const refreshTableAction = () => {
    if (pageIndex === DEFAULT_PAGE_INDEX) {
      refetch();
      return;
    }

    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  return (
    <>
      <AppsHeader>
        <Box
          flex={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <AppSearch
              isClearable
              searchValue={searchTerm}
              onClearSearch={clearSearchTerm}
              iconPosition='right'
              id='outlined-adornment-weight'
              size='small'
              overlap={false}
              onChange={onChangeSearch}
              onBlur={() => {
                isSearchTermChanged && refetch();
                setIsSearchTermChanged(false);
              }}
            ></AppSearch>
          </Box>
          <AllowedAccess
            permissionIds={[AuthorizePermission.APP_INSTANCE_CREATE]}
          >
            <Button variant='outlined' onClick={goToAdd}>
              Add New
            </Button>
          </AllowedAccess>
        </Box>
      </AppsHeader>

      <Box style={{ height: 'auto', width: '100%' }}>
        <DeleteAppInstanceAction
          open={visible}
          selected={selected}
          loading={deleteMutate?.isPending}
          handleCloseDelete={handleCloseDelete}
          handleSubmitDelete={() => {
            handleCloseDelete();
            refetch();
          }}
        />

        <DataGrid
          hideFooter
          autoHeight
          disableColumnSelector
          disableColumnMenu
          rows={rows}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
        />
      </Box>

      <FooterWrapper className='footer'>
        <Box justifyContent={'center'} className='footerContainer'>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE) || 1}
            page={pageIndex}
            onChange={(e, v) => onPageChange(v)}
            showFirstButton
            showLastButton
          />
          <Typography>Total: {totalCount || 0}</Typography>
          <Tooltip title='Refresh'>
            <IconButton
              disabled={isRefetching}
              size='small'
              sx={{ ml: 2 }}
              onClick={refreshTableAction}
            >
              <RefreshIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      </FooterWrapper>
    </>
  );
};

export default AppInstanceTable;
